'use client';
import { Box, CircularProgress, Typography } from '@mui/material';
import {
    Dispatch,
    FunctionComponent,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { ReturnParameterInFilter } from '../../../schemas/ReturnParameterInFilter';
import { DynamicRequestBody } from '../../../schemas/DynamicRequestBody';
import { ReturnParameterValueInFilter } from '../../../schemas/ReturnParameterValueInFilter';
import FilterComponent from './FilterComponent';
import { useResponsive } from '../../../contexts/ResponsiveContext';
import PageContainer from '../tableComponents/PageContainer';
import NormalFilterController from './NormalFilterController';
import { DetectedDefinition } from '../../../schemas/DetectedDefinition';
import { useLocale } from '../../../contexts/LocaleProvider';
import { LanguageMapping } from '../../../utils/LanguageMapping';
import { ProductsContent } from '../../../graphql/pimcore/modules/siteContent/types/ProductsContent';

interface FilterSectionProps {
    originData: ReturnParameterInFilter[];
    setGlobalFilterState: Dispatch<SetStateAction<DynamicRequestBody | undefined>>;
    globalFilterState: DynamicRequestBody | undefined;
    firstKeyAllowedMultipleSelect: string | null;
    productField: DetectedDefinition;
    appliedKeysSet: Set<string>;
    applyFilterResults: ReturnParameterInFilter[] | undefined;
    language: string;
    tempFilterResults: ReturnParameterInFilter[] | undefined;
    isFilterControllerOpen: boolean;
    toggleFilterController: () => void;
    // 新增：reset 訊號，當該值變化時重置內部 active keys
    resetFilterSignal: number;
    contentTree: ProductsContent;
    isLoading?: boolean;
}

interface FilterItem {
    filterKey: string;
    title: string;
    originOptions: ReturnParameterValueInFilter[];
    isActive: boolean;
    filteredOptions: ReturnParameterValueInFilter[] | undefined;
    sortIndex: number | null | undefined;
}

function buildDataMap(data: ReturnParameterInFilter[]): { [key: string]: ReturnParameterInFilter } {
    return data.reduce(
        (acc, param) => {
            // Only add to map if parameterValues has items
            if (param.parameterValues && param.parameterValues.length > 0) {
                const key = param.customColumn ? param.parameterName : param.parameterId.toString();
                acc[key] = param;
            }
            return acc;
        },
        {} as { [key: string]: ReturnParameterInFilter }
    );
}

function compareBySortIndex(a: FilterItem, b: FilterItem) {
    const aIndex = a.sortIndex ?? Infinity;
    const bIndex = b.sortIndex ?? Infinity;
    return aIndex - bIndex;
}

function generateActiveFilterKeys(
    filterEntries: [string, ReturnParameterInFilter][],
    numberOfActiveKeys: number,
    isMobile: boolean
): string[] {
    // 手機版直接全開，不做slice限制
    if (isMobile) {
        return filterEntries.map(([key]) => key);
    }

    // 過濾掉 key 為 "79" 且 parameterValues 長度等於 1 的項目
    const filteredEntries = filterEntries.filter(([key, param]) => {
        return !(key === '79' && param.parameterValues && param.parameterValues.length === 1);
    });

    const sortedEntries = [...filteredEntries].sort(([, a], [, b]) => {
        const aIndex = a.sortIndex === null || a.sortIndex === undefined ? Infinity : a.sortIndex;
        const bIndex = b.sortIndex === null || b.sortIndex === undefined ? Infinity : b.sortIndex;
        return (aIndex as number) - (bIndex as number);
    });

    return sortedEntries.slice(0, numberOfActiveKeys).map(([key]) => key);
}

const getDisplayNameForParameter = (param: ReturnParameterInFilter, language: string) => {
    const displayName = param.parameterDisplayNames?.find(
        (displayName) => displayName.language === language
    );
    return displayName?.display || param.parameterName;
};

const FilterSection: FunctionComponent<FilterSectionProps> = ({
    originData,
    setGlobalFilterState,
    globalFilterState,
    firstKeyAllowedMultipleSelect,
    productField,
    appliedKeysSet,
    applyFilterResults,
    tempFilterResults,
    isFilterControllerOpen,
    toggleFilterController,
    resetFilterSignal,
    contentTree,
    isLoading,
}) => {
    const { locale } = useLocale();

    const activeFilterZoneRef = useRef<HTMLDivElement>(null);
    const [activeFilters, setActiveFilters] = useState<FilterItem[]>([]);
    const [inactiveFilters, setInactiveFilters] = useState<FilterItem[]>([]);
    const [activeFilterKeys, setActiveFilterKeys] = useState<string[]>([]);
    const [dataMap, setDataMap] = useState<{ [key: string]: ReturnParameterInFilter }>({});

    const { isMaxScreen, isScreenXxl, isScreenMd, isScreenSm, isScreenXs, responsiveWidth } =
        useResponsive();
    // 當不是 isMaxScreen 或 isScreenXxl 時，包上 PageContainer
    const shouldWrapWithPageContainer = !(isMaxScreen || isScreenXxl);

    const isMobileSize = isScreenMd || isScreenSm || isScreenXs;

    const NUMBER_OF_ACTIVE_KEYS = isMobileSize ? 0 : 5;

    // 先建立一個 useRef 來儲存上一次 dataMap 的 key
    const prevDataMapKeysRef = useRef<string[]>([]);

    useEffect(() => {
        // dataMap 還沒準備好就跳出
        const currentDataMapKeys = Object.keys(dataMap).sort();
        if (currentDataMapKeys.length === 0) return;

        // 根據 dataMap 產生 filterEntries 與新的 active key 陣列
        const filterEntries = Object.entries(dataMap).filter(
            ([, value]) => value.parameterValues?.length
        );
        const newGeneratedActiveFilterKeys = generateActiveFilterKeys(
            filterEntries,
            NUMBER_OF_ACTIVE_KEYS,
            isMobileSize
        );

        // 比較上一次與這次的 dataMap key 是否不同
        const prevKeys = prevDataMapKeysRef.current;
        let dataMapUpdated = false;
        if (prevKeys.length !== currentDataMapKeys.length) {
            dataMapUpdated = true;
        } else {
            for (let i = 0; i < currentDataMapKeys.length; i++) {
                if (currentDataMapKeys[i] !== prevKeys[i]) {
                    dataMapUpdated = true;
                    break;
                }
            }
        }

        // 如果 dataMap 更新了，就直接更新 activeFilterKeys，
        // 否則只有在 activeFilterKeys 為空時才更新（保持原有邏輯）
        if (dataMapUpdated) {
            setActiveFilterKeys(newGeneratedActiveFilterKeys);
            prevDataMapKeysRef.current = currentDataMapKeys;
        }
    }, [resetFilterSignal, productField, dataMap, activeFilterKeys, isMobileSize]);

    useEffect(() => {
        const dataToUse = !applyFilterResults ? originData : applyFilterResults;
        if (!dataToUse) return;

        const newDataMap = buildDataMap(dataToUse);
        setDataMap(newDataMap);
    }, [originData, applyFilterResults]);

    // 利用 useMemo 將 active/inactive filters 的運算結果記憶下來
    const computedFilters = useMemo(() => {
        const filterEntries = Object.entries(dataMap).filter(([key]) => !appliedKeysSet.has(key));

        const tempFilterDataMap = tempFilterResults ? buildDataMap(tempFilterResults) : {};

        const filterMap: Map<string, FilterItem> = new Map(
            filterEntries.map(([key, value]) => {
                const title = getDisplayNameForParameter(
                    value,
                    LanguageMapping.get(locale) || 'ENGLISH'
                );
                const originParam = dataMap[key];
                const filteredParam = tempFilterDataMap[key];

                const originOptions = originParam ? originParam.parameterValues || [] : [];
                const filteredOptions = filteredParam
                    ? filteredParam.parameterValues || []
                    : undefined;

                return [
                    key,
                    {
                        filterKey: key,
                        title,
                        originOptions,
                        isActive: activeFilterKeys.includes(key),
                        filteredOptions:
                            key === firstKeyAllowedMultipleSelect ? undefined : filteredOptions,
                        sortIndex: value.sortIndex,
                    },
                ];
            })
        );

        const tempActiveFilters: FilterItem[] = [];
        const tempInactiveFilters: FilterItem[] = [];

        // 先把 activeFilterKeys 裡的項目，從 filterMap 拿出來塞到 tempActiveFilters
        activeFilterKeys.forEach((key) => {
            const item = filterMap.get(key);
            if (item !== undefined) {
                tempActiveFilters.push(item);
            } else {
                // console.warn(`%c[computedFilters] key=${key} not found in filterMap`, 'color:red;');
            }
        });

        // 剩下沒在 activeFilterKeys 的項目，就當作 inactive
        filterEntries.forEach(([key]) => {
            if (!activeFilterKeys.includes(key)) {
                const item = filterMap.get(key);
                if (item) {
                    tempInactiveFilters.push(item);
                }
            }
        });

        tempActiveFilters.sort(compareBySortIndex);
        tempInactiveFilters.sort(compareBySortIndex);

        // 如果不滿五個，就從 inactive 補上
        while (tempActiveFilters.length < NUMBER_OF_ACTIVE_KEYS && tempInactiveFilters.length > 0) {
            const newFilter = tempInactiveFilters.shift()!;
            tempActiveFilters.push({ ...newFilter, isActive: true });
        }

        return { active: tempActiveFilters, inactive: tempInactiveFilters };
    }, [
        dataMap,
        firstKeyAllowedMultipleSelect,
        appliedKeysSet,
        activeFilterKeys,
        tempFilterResults,
        LanguageMapping.get(locale) || 'ENGLISH',
        isMobileSize,
    ]);

    // 當 computedFilters 改變時，再更新 state
    useEffect(() => {
        setActiveFilters(computedFilters.active);
        setInactiveFilters(computedFilters.inactive);
    }, [computedFilters]);

    const handleActivateFilter = useCallback(
        (filterKey: string) => {
            setActiveFilterKeys((prevKeys) => {
                return [...prevKeys, filterKey];
            });

            setActiveFilters((prevActiveFilters) => {
                const filterToActivate = inactiveFilters.find(
                    (filter) => filter.filterKey === filterKey
                );
                return filterToActivate
                    ? [...prevActiveFilters, { ...filterToActivate, isActive: true }]
                    : prevActiveFilters;
            });

            setInactiveFilters((prevInactiveFilters) =>
                prevInactiveFilters.filter((filter) => filter.filterKey !== filterKey)
            );

            setTimeout(() => {
                if (activeFilterZoneRef.current) {
                    activeFilterZoneRef.current.scrollLeft =
                        activeFilterZoneRef.current.scrollWidth;
                }
            }, 100);
        },
        [inactiveFilters]
    );

    const handleDeactivateFilter = useCallback(
        (filterKey: string) => {
            setActiveFilters((prevActiveFilters) => {
                return prevActiveFilters.filter((filter) => filter.filterKey !== filterKey);
            });

            setInactiveFilters((prevInactiveFilters) => {
                const filterToDeactivate = activeFilters.find(
                    (filter) => filter.filterKey === filterKey
                );
                return filterToDeactivate
                    ? [...prevInactiveFilters, { ...filterToDeactivate, isActive: false }]
                    : prevInactiveFilters;
            });

            setActiveFilterKeys((prevKeys) => {
                return prevKeys.filter((key) => key !== filterKey);
            });

            setTimeout(() => {
                if (activeFilterZoneRef.current) {
                    if (activeFilterKeys.length === 5) {
                        activeFilterZoneRef.current.scrollLeft = 0;
                    }
                }
            }, 100);
        },
        [activeFilters, activeFilterKeys]
    );

    const mobileFilters = useMemo(() => {
        const filterEntries = Object.entries(dataMap).filter(([key]) => !appliedKeysSet.has(key));
        return filterEntries
            .map(([key, value]) => {
                const title = getDisplayNameForParameter(
                    value,
                    LanguageMapping.get(locale) || 'ENGLISH'
                );
                const originParam = dataMap[key];
                // 如果 tempFilterResults 有值，透過 buildDataMap 取得對應的 filtered data
                const tempDataMap = tempFilterResults ? buildDataMap(tempFilterResults) : {};
                const filteredParam = tempDataMap[key];
                const originOptions = originParam ? originParam.parameterValues || [] : [];
                const filteredOptions = filteredParam
                    ? filteredParam.parameterValues || []
                    : undefined;
                return {
                    filterKey: key,
                    title,
                    originOptions,
                    isActive: activeFilterKeys.includes(key),
                    filteredOptions:
                        key === firstKeyAllowedMultipleSelect ? undefined : filteredOptions,
                    sortIndex: value.sortIndex,
                };
            })
            .sort(compareBySortIndex);
    }, [
        dataMap,
        appliedKeysSet,
        activeFilterKeys,
        tempFilterResults,
        LanguageMapping.get(locale) || 'ENGLISH',
        firstKeyAllowedMultipleSelect,
    ]);

    const getZoneWidths = (responsiveWidth: string, inactiveFiltersLength: number) => {
        if (inactiveFiltersLength === 0) {
            return { activeZoneWidth: '100%', inactiveZoneWidth: '0%' };
        }
        if (responsiveWidth === '1185px') {
            return { activeZoneWidth: '68%', inactiveZoneWidth: '32%' };
        }
        if (responsiveWidth === '1125px') {
            return { activeZoneWidth: '77%', inactiveZoneWidth: '23%' };
        }

        if (responsiveWidth === '885px') {
            return { activeZoneWidth: '68%', inactiveZoneWidth: '32%' };
        } else {
            return { activeZoneWidth: '68%', inactiveZoneWidth: '32%' };
        }
    };

    const { activeZoneWidth, inactiveZoneWidth } = getZoneWidths(
        responsiveWidth,
        inactiveFilters.length
    );

    const content = (
        <Box
            sx={{
                // Outer container to hold the "Open / Close Filters" button + filter zones
                width: '100%',
                backgroundColor: '#F2F2F2',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                padding: shouldWrapWithPageContainer ? '0' : '0.5rem 1rem 0rem 1rem',
                borderRadius: '10px',
            }}
            data-testid={'filter-section'}
        >
            {!isMobileSize && (
                <NormalFilterController
                    isFilterControllerOpen={isFilterControllerOpen}
                    toggleFilterController={toggleFilterController}
                    contentTree={contentTree}
                />
            )}
            {isFilterControllerOpen && (
                <Box
                    sx={{
                        // Container for the active & inactive zones
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '1rem',
                        marginTop: '1rem',
                    }}
                    data-testid={'all_filters_zone'}
                >
                    <Box
                        ref={activeFilterZoneRef}
                        data-testid={'active_filter_zone'}
                        sx={{
                            width: activeZoneWidth,
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'flex-start',
                            gap: '1.5rem',
                            boxSizing: 'border-box',
                            overflowX: 'auto',
                            overflowY: 'hidden',
                            height: '380px',
                            paddingBottom: '1rem',

                            '& > *': {
                                // Each filter card has a fixed width from Figma: 225px
                                flex: '0 0 225px',
                                maxWidth: '225px',
                                flexShrink: 0,
                            },

                            '::-webkit-scrollbar': {
                                height: '1rem',
                            },
                            '::-webkit-scrollbar-track': {
                                background: 'white',
                                borderRadius: '20px',
                            },
                            '::-webkit-scrollbar-thumb': {
                                backgroundColor: '#B75332',
                                borderRadius: '20px',
                                border: '2px solid white', // 調整邊框寬度以控制 thumb 尺寸
                                backgroundClip: 'padding-box', // 確保背景不延伸到邊框之外
                            },
                        }}
                    >
                        {activeFilters.map((filter, index) => (
                            <FilterComponent
                                key={index}
                                isFilterActive={filter.isActive}
                                title={filter.title}
                                originOptions={filter.originOptions}
                                setGlobalFilterState={setGlobalFilterState}
                                filteredOptions={filter.filteredOptions}
                                globalFilterState={globalFilterState}
                                onDeactivateFilter={handleDeactivateFilter}
                                firstKeyAllowedMultipleSelect={firstKeyAllowedMultipleSelect}
                                filterKey={filter.filterKey}
                                language={LanguageMapping.get(locale) || 'ENGLISH'}
                            />
                        ))}
                    </Box>

                    {inactiveZoneWidth !== '0%' && (
                        <Box
                            data-testid={'inactive_filter_zone'}
                            sx={{
                                width: inactiveZoneWidth,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                boxSizing: 'border-box',
                                maxHeight: '400px',
                                overflowY: 'auto',

                                '::-webkit-scrollbar': {
                                    height: '1rem',
                                },
                                '::-webkit-scrollbar-track': {
                                    background: 'white',
                                    borderRadius: '20px',
                                },
                                '::-webkit-scrollbar-thumb': {
                                    backgroundColor: '#B75332',
                                    borderRadius: '20px',
                                    border: '2px solid white', // 調整邊框寬度以控制 thumb 尺寸
                                    backgroundClip: 'padding-box', // 確保背景不延伸到邊框之外
                                },
                            }}
                        >
                            <Typography
                                variant={'h6'}
                                sx={{
                                    fontFamily: 'Poppins',
                                    fontWeight: 600,
                                    fontSize: 16,
                                    lineHeight: '1.125rem',
                                    marginBottom: '20px',
                                    marginTop: '1rem',
                                }}
                            >
                                {contentTree.filters.labels.additionalFilters.content}
                            </Typography>

                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    gap: '10px',
                                    flexWrap: 'wrap',
                                    justifyContent: 'center',
                                    paddingBottom: '1rem',
                                }}
                            >
                                {inactiveFilters.map((filter, index) => (
                                    <FilterComponent
                                        key={index}
                                        isFilterActive={filter.isActive}
                                        title={filter.title}
                                        originOptions={filter.originOptions}
                                        setGlobalFilterState={setGlobalFilterState}
                                        filteredOptions={filter.filteredOptions}
                                        globalFilterState={globalFilterState}
                                        onActivateFilter={handleActivateFilter}
                                        firstKeyAllowedMultipleSelect={
                                            firstKeyAllowedMultipleSelect
                                        }
                                        filterKey={filter.filterKey}
                                        language={LanguageMapping.get(locale) || 'ENGLISH'}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );

    const mobileContent = (
        <Box
            sx={{
                width: '100%',
                backgroundColor: 'white',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
            }}
        >
            {isFilterControllerOpen &&
                mobileFilters.map((filter) => (
                    <FilterComponent
                        key={filter.filterKey}
                        isFilterActive={filter.isActive}
                        title={filter.title}
                        originOptions={filter.originOptions}
                        setGlobalFilterState={setGlobalFilterState}
                        filteredOptions={filter.filteredOptions}
                        globalFilterState={globalFilterState}
                        {...(filter.isActive
                            ? { onDeactivateFilter: handleDeactivateFilter }
                            : { onActivateFilter: handleActivateFilter })}
                        firstKeyAllowedMultipleSelect={firstKeyAllowedMultipleSelect}
                        filterKey={filter.filterKey}
                        language={LanguageMapping.get(locale) || 'ENGLISH'}
                    />
                ))}
        </Box>
    );

    // Loading 節點（沿用你給的樣式 + 多語字串 fallback）
    const loadingNode = (
        <Box
            key="filter-section-loading"
            sx={{
                backgroundColor: '#F2F2F2',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                margin: 'auto',
                height: '400px',
                width: '100%',
                borderRadius: '10px',
            }}
        >
            <CircularProgress size={180} sx={{ color: '#E5673E' }} />
        </Box>
    );

    if (isLoading) {
        return shouldWrapWithPageContainer ? (
            <PageContainer
                sx={{ padding: 0 }}
                innerSx={{ width: responsiveWidth }}
                containerName={'filter-responsive'}
            >
                {loadingNode}
            </PageContainer>
        ) : (
            loadingNode
        );
    }

    return shouldWrapWithPageContainer ? (
        <PageContainer
            sx={{ padding: 0 }}
            innerSx={{ width: responsiveWidth }}
            containerName={'filter-responsive'}
        >
            {isMobileSize ? mobileContent : content}
        </PageContainer>
    ) : (
        content
    );
};

export default FilterSection;
